import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { showSystemToast } from "../utils/system_toast"

// List-level toggles in the tasks header. The tasks outline lives in a
// separate Stimulus element, so the fold / copy actions reach it via window
// events (task-list-menu:fold / :copy) that the outline listens for. Rename is
// a self-contained inline PATCH.

export default class extends Controller {
    static targets = ["renameForm", "renameInput"]
    static values = { updateUrl: String }

    // Copy all task + subtask text. The outline owns the DOM, so it does the
    // actual clipboard write on this window event (fired within the click
    // gesture, before split-menu#choose closes the menu).
    copyText() {
        this.dispatch("copy", { target: window })
    }

    // The outline's keyboard-shortcut sheet.
    shortcuts() {
        this.dispatch("shortcuts", { target: window })
    }

    // Pop this list out into its own window, named per list so re-opening the
    // same list focuses the existing window instead of stacking duplicates.
    // Modifier-clicks are left to the browser, and if the popup is blocked we
    // don't preventDefault — the link's target="_blank" opens a tab instead.
    openWindow(event) {
        const link = event.currentTarget
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        const name = link.dataset.windowName || "_blank"
        const win = window.open(link.href, name, "popup,width=560,height=900")
        if (!win) return
        event.preventDefault()
        win.focus()
    }

    // --- Inline rename ---
    // Reveals a text field inside the still-open menu (startRename never fires
    // split-menu#choose), then PATCHes the list name on Enter.

    startRename(event) {
        event.preventDefault()
        event.stopPropagation()
        if (!this.hasRenameFormTarget) return
        this.renameFormTarget.hidden = false
        if (!this.hasRenameInputTarget) return
        const input = this.renameInputTarget
        requestAnimationFrame(() => { input.focus(); input.select() })
    }

    renameKeydown(event) {
        // Keep the outline's keyboard shortcuts and the menu's Escape-to-close
        // from firing while typing a new name.
        event.stopPropagation()
        if (event.key === "Enter") {
            event.preventDefault()
            this._submitRename()
        } else if (event.key === "Escape") {
            event.preventDefault()
            this._cancelRename()
        }
    }

    _cancelRename() {
        if (this.hasRenameFormTarget) this.renameFormTarget.hidden = true
        this._closeMenu()
    }

    _submitRename() {
        if (!this.updateUrlValue || !this.hasRenameInputTarget) return
        const name = (this.renameInputTarget.value || "").trim()
        if (!name) return
        const fd = new FormData()
        fd.set("name", name)
        fetch(this.updateUrlValue, {
            method: "PATCH",
            headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" },
            body: fd
        })
            .then((r) => r.json().catch(() => ({})))
            .then((data) => {
                if (!data?.ok) {
                    showSystemToast(data?.error || "Could not rename the list.", { level: "warning" })
                    return
                }
                this._applyName(data.list?.name || name)
                showSystemToast("List renamed")
            })
            .catch(() => showSystemToast("Could not rename the list.", { level: "warning" }))
    }

    // The list's icon (TaskList#emoji), from the emoji picker in this menu.
    // It shows in several places (the switcher, the Pinned row), so a reload
    // redraws them all rather than patching each.
    setEmoji(event) {
        const emoji = event.detail?.emoji
        if (emoji) this._saveEmoji(emoji)
    }

    clearEmoji(event) {
        event?.preventDefault()
        this._saveEmoji("")
    }

    _saveEmoji(emoji) {
        if (!this.updateUrlValue) return
        const fd = new FormData()
        fd.set("emoji", emoji)
        fetch(this.updateUrlValue, {
            method: "PATCH",
            headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" },
            body: fd
        })
            .then((r) => r.json().catch(() => ({})))
            .then((data) => {
                if (!data?.ok) {
                    showSystemToast(data?.error || "Could not change the list's icon.", { level: "warning" })
                    return
                }
                window.location.reload()
            })
            .catch(() => showSystemToast("Could not change the list's icon.", { level: "warning" }))
    }

    // Reflect the new name where it shows without a full reload: the header
    // trigger and the current row in the switcher modal. Anything else refreshes
    // on next nav. The row's data-name drives the switcher's text filter, so it
    // has to move too or the list stops matching its own new name.
    _applyName(name) {
        const brand = document.querySelector(".task-list-picker-trigger .assistant-brand")
        if (brand) brand.textContent = name
        const currentRow = document.querySelector(".list-switcher-item.is-current")
        if (currentRow) {
            const label = currentRow.querySelector(".list-switcher-item-name")
            if (label) label.textContent = name
            currentRow.dataset.name = name.toLowerCase()
        }
        if (this.hasRenameInputTarget) this.renameInputTarget.value = name
        this._cancelRename()
    }

    _closeMenu() {
        const split = this.application.getControllerForElementAndIdentifier(this.element, "split-menu")
        if (split) split.close()
    }

    // One round button that alternates fold-all / unfold-all. Best-effort
    // state (a manual expand can drift it), same as the source list apps.
    toggleFold(event) {
        const btn = event.currentTarget
        const folded = btn.dataset.folded !== "true"
        this.dispatch("fold", { target: window, detail: { folded } })
        btn.dataset.folded = String(folded)
        btn.classList.toggle("is-active", folded)
        btn.setAttribute("aria-pressed", String(folded))
        const label = folded ? "Unfold all" : "Fold all"
        btn.title = label
        btn.setAttribute("aria-label", label)
        // The kebab-menu row (phone) carries a text label the round button lacks.
        const text = btn.querySelector("[data-fold-label]")
        if (text) text.textContent = label
        const icon = btn.querySelector("i")
        if (icon) icon.className = folded ? "bi bi-chevron-expand" : "bi bi-chevron-contract"
    }

    // Realtime sync indicator/pause toggle. Session-scoped (no persistence,
    // always starts live on load) — the outline controller owns the actual
    // pause state and decides whether a resume needs a refresh; this button
    // just reflects paused/live and a skipped-updates count it hears back.
    toggleLive(event) {
        const btn = event.currentTarget
        const paused = btn.dataset.tasksLivePaused !== "true"
        this._applyLiveState(btn, paused)
        this.dispatch("live", { target: window, detail: { paused } })
    }

    // A @window action's currentTarget is window itself, not this button —
    // use the controller's own element (this button is its own instance).
    onLiveSkip(event) {
        this._applyLiveBadge(this.element, event.detail?.count || 0)
    }

    _applyLiveState(btn, paused) {
        btn.dataset.tasksLivePaused = String(paused)
        btn.classList.toggle("is-paused", paused)
        btn.setAttribute("aria-pressed", String(paused))
        const label = paused ? "Live sync paused — click to resume" : "Live sync on — click to pause"
        btn.title = label
        btn.setAttribute("aria-label", label)
        const text = btn.querySelector("[data-live-label]")
        if (text) text.textContent = paused ? "Resume live sync" : "Pause live sync"
        this._applyLiveBadge(btn, 0)
    }

    _applyLiveBadge(btn, count) {
        const badge = btn.querySelector("[data-live-badge]")
        if (!badge) return
        if (count > 0) {
            badge.textContent = count > 9 ? "9+" : String(count)
            badge.hidden = false
        } else {
            badge.hidden = true
        }
    }
}
